import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import Header from '../home/Header';
import Footer from '../home/Footer';

const ProsthesisteOngulaire = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Prothésiste Ongulaire : Formation, Métier et Réservation",
    "description": "Découvrez le métier de prothésiste ongulaire : formation, compétences, tarifs. Trouvez et réservez les meilleurs professionnels près de chez vous sur BookingBarber.",
    "author": {
      "@type": "Organization",
      "name": "BookingBarber"
    },
    "publisher": {
      "@type": "Organization",
      "name": "BookingBarber",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bookingbarber.com/Logos/logoFocus.png"
      }
    },
    "datePublished": "2025-01-08",
    "dateModified": "2025-01-08",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://bookingbarber.com/posts/prothesiste-ongulaire"
    },
    "about": {
      "@type": "Occupation",
      "name": "Prothésiste Ongulaire",
      "description": "Professionnel spécialisé dans la pose et la décoration d'ongles artificiels",
      "skills": ["Pose d'ongles en gel", "Nail art", "Manucure", "Pédicure", "Extension d'ongles"]
    }
  };

  const handleBooking = () => {
    navigate('/search?category=Manucure&professional=prothesiste-ongulaire');
  };

  const services = [
    "Pose d'ongles en gel",
    "Extension d'ongles",
    "Nail art",
    "Manucure française",
    "Réparation d'ongles",
    "Dépose d'ongles",
    "Soins des cuticules",
    "Pédicure"
  ];

  return (
    <>
      <SEOHead
        title="Prothésiste Ongulaire : Formation, Métier et Réservation | BookingBarber"
        description="🌟 Découvrez le métier de prothésiste ongulaire : formation, compétences, tarifs. Trouvez et réservez les meilleurs professionnels près de chez vous sur BookingBarber. Service de réservation beauté le moins cher au monde."
        keywords="prothésiste ongulaire, formation prothésiste ongulaire, métier prothésiste ongulaire, pose d'ongles gel, nail art, extension ongles, manucure professionnelle, réservation prothésiste ongulaire, prothésiste ongulaire prix, prothésiste ongulaire Paris"
        canonicalUrl="https://bookingbarber.com/posts/prothesiste-ongulaire"
        structuredData={structuredData}
      />
      
      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h1" component="h1" sx={{ 
            fontSize: { xs: '2rem', md: '3rem' }, 
            fontWeight: 'bold', 
            mb: 2,
            background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Prothésiste Ongulaire : Métier et Formation
          </Typography>
          <Typography variant="h2" component="h2" sx={{ 
            fontSize: '1.2rem', 
            color: 'text.secondary', 
            mb: 4 
          }}>
            Tout savoir sur le métier de prothésiste ongulaire et comment trouver le meilleur professionnel
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            onClick={handleBooking}
            sx={{ 
              background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
              px: 4,
              py: 1.5
            }}
          >
            Trouver un Prothésiste Ongulaire
          </Button>
        </Box>

        {/* Content Grid */}
        <Grid container spacing={4}>
          {/* Main Content */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h3" component="h3" sx={{ mb: 3, fontSize: '1.5rem', fontWeight: 'bold' }}>
                  Qu'est-ce qu'un Prothésiste Ongulaire ?
                </Typography>
                <Typography paragraph>
                  Un prothésiste ongulaire est un professionnel spécialisé dans la pose, la décoration et l'entretien 
                  d'ongles artificiels. Ce métier allie technique, créativité et sens esthétique pour sublimer 
                  les mains de ses clients.
                </Typography>
                
                <Typography variant="h4" component="h4" sx={{ mt: 4, mb: 2, fontSize: '1.3rem', fontWeight: 'bold' }}>
                  Compétences et Services d'un Prothésiste Ongulaire
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {services.map((service, index) => (
                    <Chip 
                      key={index}
                      label={service}
                      sx={{ m: 0.5, bgcolor: '#f3f4f6' }}
                    />
                  ))}
                </Box>

                <Typography variant="h4" component="h4" sx={{ mt: 4, mb: 2, fontSize: '1.3rem', fontWeight: 'bold' }}>
                  Formation Prothésiste Ongulaire
                </Typography>
                <Typography paragraph>
                  Pour devenir prothésiste ongulaire, plusieurs voies de formation sont possibles :
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" paragraph>
                    <strong>CAP Esthétique-Cosmétique-Parfumerie :</strong> Formation de base recommandée
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Formations spécialisées :</strong> Stages intensifs de 1 à 4 semaines
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Formations en ligne :</strong> Apprentissage théorique et pratique à distance
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Perfectionnement :</strong> Formations continues pour maîtriser nouvelles techniques
                  </Typography>
                </Box>

                <Typography variant="h4" component="h4" sx={{ mt: 4, mb: 2, fontSize: '1.3rem', fontWeight: 'bold' }}>
                  Tarifs et Prix des Prestations
                </Typography>
                <Typography paragraph>
                  Les tarifs d'un prothésiste ongulaire varient selon plusieurs facteurs :
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li"><strong>Pose complète gel :</strong> 25€ - 60€</Typography>
                  <Typography component="li"><strong>Remplissage :</strong> 20€ - 40€</Typography>
                  <Typography component="li"><strong>Nail art simple :</strong> +5€ - 15€</Typography>
                  <Typography component="li"><strong>Nail art complexe :</strong> +15€ - 30€</Typography>
                  <Typography component="li"><strong>Dépose :</strong> 10€ - 20€</Typography>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" component="h4" sx={{ mb: 3, fontSize: '1.3rem', fontWeight: 'bold' }}>
                  Comment Choisir son Prothésiste Ongulaire ?
                </Typography>
                <Typography paragraph>
                  Pour choisir le bon professionnel, voici les critères essentiels :
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  <Typography component="li" paragraph>
                    <strong>Qualifications :</strong> Diplômes et certifications professionnelles
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Expérience :</strong> Années de pratique et spécialisations
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Portfolio :</strong> Photos de réalisations récentes
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Hygiène :</strong> Respect des normes sanitaires strictes
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Avis clients :</strong> Évaluations et témoignages
                  </Typography>
                  <Typography component="li" paragraph>
                    <strong>Matériel :</strong> Utilisation de produits de qualité professionnelle
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h5" component="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Trouvez Votre Prothésiste
                </Typography>
                <Typography paragraph sx={{ fontSize: '0.9rem' }}>
                  Découvrez les meilleurs prothésistes ongulaires près de chez vous. 
                  Comparez les prix, consultez les avis et réservez en ligne.
                </Typography>
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={handleBooking}
                  sx={{ 
                    background: 'linear-gradient(45deg, #6366f1, #8b5cf6)',
                    mb: 2
                  }}
                >
                  Voir les Professionnels
                </Button>
              </CardContent>
            </Card>

            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" component="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Techniques Populaires
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate('/posts/pose-ongles-gel')}
                  >
                    Pose d'Ongles en Gel
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate('/search?category=Manucure&service=extension-ongles')}
                  >
                    Extension d'Ongles
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => navigate('/search?category=Manucure&service=nail-art')}
                  >
                    Nail Art Créatif
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" component="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Pourquoi BookingBarber ?
                </Typography>
                <Box component="ul" sx={{ pl: 2, fontSize: '0.9rem' }}>
                  <Typography component="li">Prix les plus bas du marché</Typography>
                  <Typography component="li">Professionnels vérifiés</Typography>
                  <Typography component="li">Réservation en ligne 24/7</Typography>
                  <Typography component="li">Avis clients authentiques</Typography>
                  <Typography component="li">Paiement sécurisé</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
      
      <Footer />
    </>
  );
};

export default ProsthesisteOngulaire;